import { Controller } from '@hotwired/stimulus';
import { useDebounce } from 'stimulus-use';

// Handle automatic reloading of the step3 estimation form by adding another hidden submit button.
// The form can be submitted in three ways:
// 1. click on the (visible) submit button ("Je continue")
// 2. hit “return” in an input.
// 3. change value in a select
// Case 1 is the only one where we want to continue to the next step;
// in case 2 and 3, we want to recompute the estimation, save it to DB, and display step3 again.
// This is achieved by:
// - using another “reload” submit button, inserted at the start of the form DOM tree.
//   This means that when the user hits return, the form will be submit with this button.
// - listening to onchange events on the form, and submit with the reload button
//   onchange events bubble, which means we don’t need to observe all inputs individually.
// - adding a preSubmit on the submit event, that doesn’t stop the submit
//   but adds a “do_not_advance=true” parameter.
export default class extends Controller {
  static targets = ['submitButton', 'inputsDiv']

  static debounces = [{ name: 'textInput', wait: 500 }];

  connect() {
    if (!(this.element instanceof HTMLFormElement)) {
      throw TypeError("Element of this controller must be the form")
    }

    this.reloadButton = document.createElement('input')
    this.reloadButton.type = 'submit'
    this.reloadButton.name = 'reload'
    this.reloadButton.hidden = true
    this.reloadButton.dataset.turboAction = "replace"
    this.element.insertBefore(this.reloadButton, this.element.firstChild)

    // All changes in the form are equivalent to clicking the reload button.
    // Additionally, since the reload button is the first input type=submit in the form,
    // it is also triggered when pressing Enter in text fields.
    this.inputsDivTarget.addEventListener('change', () => this.element.requestSubmit(this.reloadButton))
    this.element.addEventListener('submit', (e) => this.#beforeSubmit(e))
    useDebounce(this);
    this.inputsDivTarget.addEventListener('input', () => this.textInput())

    // Save and restore focus when the page is reloaded by turbo.
    // Unfortunately, we can’t use turbo morph, which would do that for free.
    this.#restoreFocus()
    this.element.addEventListener('focusin', (e) => this.activeElement = e.target)
    this.element.addEventListener('focusout', () => this.activeElement = null)
  }

  disconnect() {
    this.#saveFocus()
  }

  textInput() {
    if (this.submitButtonTarget.disabled) {
      return
    }
    this.element.requestSubmit(this.reloadButton)
  }

  #beforeSubmit(e) {
    // if we’re submitting via the reload button, add do_not_advance.
    // We’re not stopping propagation; submit continues eventually.
    if(e.submitter === this.reloadButton) {
      const dna = document.createElement('input')
      dna.type = 'hidden'
      dna.name = 'do_not_advance'
      dna.value = 'true'
      window.souscriptionClick('Etape 3 - Estimation', 'J\'actualise mon estimation tarifaire', 'Bouton')
      this.element.appendChild(dna)

      this.submitButtonTarget.disabled = true
    }
  }

  #saveFocus() {
    if (this.activeElement) {
      document.step3PreviouslyActive = {
        id: this.activeElement.id,
        selection: {
          start: this.activeElement.selectionStart,
          end: this.activeElement.selectionEnd,
          direction: this.activeElement.selectionDirection
        }
      }
    }
  }

  // eslint-disable-next-line class-methods-use-this
  #restoreFocus() {
    const previouslyActive = document.step3PreviouslyActive;
    if (previouslyActive === undefined) {
      return
    }
    const element = document.getElementById(previouslyActive.id)
    if (element === null) {
      return
    }

    element.focus()
    if (element.setSelectionRange) {
      element.setSelectionRange(previouslyActive.selection.start,
        previouslyActive.selection.end,
        previouslyActive.selection.direction)
    }
  }
}
